import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Button, Form, Alert, Spinner, Badge } from "react-bootstrap";
import Select from "react-select";
import {
  FaUserPlus,
  FaSearch,
  FaCheck,
  FaCopy,
  FaUsers,
  FaUser,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import axios from "../../context/AxiosInstance";
import { toast } from "react-toastify";
import "./CreerComptePersonnelModal.css";

const LOGIN_URL = "https://rh.aguipex.gov.gn/login";

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const formatOptionLabel = ({ label, matricule, hasCompte }) => (
  <div className="ccp-option">
    <span className="ccp-option-name">{label}</span>
    <span className="ccp-option-mat">
      {matricule}
      {hasCompte ? " · Compte existant" : ""}
    </span>
  </div>
);

const previewLogin = (emp) =>
  `${(emp.nom || "").toLowerCase().replace(/\s+/g, "")}.${(emp.prenom || "").toLowerCase().replace(/\s+/g, "")}`;

const filterEmployeOption = (option, inputValue) => {
  if (!inputValue) return true;
  const q = inputValue.toLowerCase().trim();
  const e = option.employe;
  if (!e) return option.label.toLowerCase().includes(q);
  const haystack = [e.nom, e.prenom, e.matricule_employe, e.departement, e.contact]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const CreerComptePersonnelModal = ({ show, onHide, onSuccess }) => {
  const [mode, setMode] = useState("single"); // single | multiple
  const [step, setStep] = useState("select"); // select | confirm | results

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [employes, setEmployes] = useState([]);
  const [comptesExistants, setComptesExistants] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDispo, setFilterDispo] = useState("all"); // all | dispo | compte

  const [createdSingle, setCreatedSingle] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [resEmp, resPers] = await Promise.all([
        axios.get("/employes/"),
        axios.get("/personnel/liste/"),
      ]);
      const actifs = (resEmp.data?.data || []).filter((e) => e.is_active);
      setEmployes(actifs);
      const ids = new Set(
        (resPers.data?.data || [])
          .map((u) => u.id_employe)
          .filter((id) => id != null)
      );
      setComptesExistants(ids);
    } catch {
      toast.error("Erreur lors du chargement des employés");
      setEmployes([]);
      setComptesExistants(new Set());
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (show) loadData();
  }, [show, loadData]);

  const hasCompte = (id) => comptesExistants.has(id);

  const employeOptions = useMemo(
    () =>
      employes.map((emp) => ({
        value: emp.id_employe,
        label: `${emp.nom} ${emp.prenom}`,
        matricule: emp.matricule_employe,
        employe: emp,
        hasCompte: hasCompte(emp.id_employe),
        isDisabled: hasCompte(emp.id_employe),
      })),
    [employes, comptesExistants]
  );

  const employesDisponibles = useMemo(
    () => employes.filter((e) => !hasCompte(e.id_employe)),
    [employes, comptesExistants]
  );

  const employesFiltres = useMemo(() => {
    const q = search.toLowerCase().trim();
    return employes.filter((e) => {
      if (filterDispo === "dispo" && hasCompte(e.id_employe)) return false;
      if (filterDispo === "compte" && !hasCompte(e.id_employe)) return false;
      if (!q) return true;
      const hay = [e.nom, e.prenom, e.matricule_employe, e.departement, e.contact]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [employes, search, filterDispo, comptesExistants]);

  const employesDisponiblesFiltres = employesFiltres.filter((e) => !hasCompte(e.id_employe));

  const selectedEmployes = useMemo(
    () => employes.filter((e) => selectedIds.includes(e.id_employe)),
    [employes, selectedIds]
  );

  const selectedEmploye = selectedOption?.employe ?? null;

  const stats = useMemo(
    () => ({
      total: employes.length,
      dispo: employesDisponibles.length,
      compte: employes.length - employesDisponibles.length,
    }),
    [employes, employesDisponibles]
  );

  const toggleEmploye = (emp) => {
    if (hasCompte(emp.id_employe)) {
      toast.warning(
        `${emp.nom} ${emp.prenom} possède déjà un compte personnel — sélection impossible.`
      );
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(emp.id_employe)
        ? prev.filter((id) => id !== emp.id_employe)
        : [...prev, emp.id_employe]
    );
  };

  const selectAllDisponibles = () => {
    const ids = employesDisponiblesFiltres.map((e) => e.id_employe);
    setSelectedIds(ids);
    if (ids.length === 0) toast.info("Aucun employé disponible dans la liste affichée.");
  };

  const clearSelection = () => setSelectedIds([]);

  const goConfirm = () => {
    setError(null);
    if (mode === "single") {
      if (!selectedOption?.value) {
        setError("Veuillez sélectionner un employé.");
        return;
      }
      if (hasCompte(selectedOption.value)) {
        setError("Cet employé possède déjà un compte personnel.");
        return;
      }
    } else {
      if (selectedIds.length === 0) {
        setError("Sélectionnez au moins un employé sans compte existant.");
        return;
      }
    }
    setStep("confirm");
  };

  const runCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === "single") {
        const response = await axios.post("/personnel/creer-compte-personnel/", {
          id_employe: selectedOption.value,
        });
        if (response.data.success) {
          const d = response.data.data;
          setCreatedSingle({
            ...d,
            nom: d.nom || selectedEmploye?.nom,
            prenom: d.prenom || selectedEmploye?.prenom,
          });
          setStep("results");
          toast.success("Compte personnel créé");
        }
      } else {
        const response = await axios.post("/personnel/creer-comptes-personnel/", {
          id_employes: selectedIds,
        });
        setBulkResults(response.data);
        setStep("results");
        if (response.data.created > 0) {
          toast.success(`${response.data.created} compte(s) créé(s)`);
        }
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} échec(s) lors de la création`);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la création";
      setError(typeof msg === "string" ? msg : "Erreur lors de la création");
      setStep("select");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const copyAllCredentials = (items) => {
    const lines = items
      .filter((r) => r.success && r.data)
      .map(
        (r) =>
          `${r.data.nom} ${r.data.prenom} (${r.data.matricule || "—"})\n` +
          `Login: ${r.data.login}\n` +
          `Mot de passe: ${r.data.mot_de_passe_temp}\n` +
          `Code bulletin: ${r.data.code_controle}\n` +
          `Lien: ${LOGIN_URL}\n`
      );
    copyText(lines.join("\n---\n"), "Tous les identifiants");
  };

  const resetState = () => {
    setMode("single");
    setStep("select");
    setSelectedOption(null);
    setSelectedIds([]);
    setError(null);
    setSearch("");
    setFilterDispo("all");
    setCreatedSingle(null);
    setBulkResults(null);
    setLoadingData(true);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleDone = () => {
    const hadSuccess = createdSingle || (bulkResults?.created > 0);
    resetState();
    if (hadSuccess) onSuccess();
    else onHide();
  };

  const selectionCount = mode === "single" ? (selectedOption ? 1 : 0) : selectedIds.length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      className="ccp-modal"
      dialogClassName={`ccp-modal-dialog ${mode === "multiple" ? "ccp-modal-dialog--wide" : ""}`}
    >
      <Modal.Header closeButton className="ccp-modal-header">
        <div className="ccp-header-inner">
          <div className="ccp-modal-title-wrap">
            <div className="ccp-modal-icon">
              <FaUserPlus />
            </div>
            <div>
              <Modal.Title className="ccp-modal-title">
                Créer {mode === "multiple" ? "des comptes" : "un compte"} Personnel
              </Modal.Title>
              <p className="ccp-modal-subtitle">
                {step === "select" && "Choisissez le ou les employés éligibles"}
                {step === "confirm" && "Vérifiez avant de confirmer la création"}
                {step === "results" && "Copiez et transmettez les identifiants"}
              </p>
            </div>
          </div>
          <div className="ccp-step-dots" aria-hidden>
            <span className={step === "select" ? "active" : step !== "select" ? "done" : ""} />
            <span className={step === "confirm" ? "active" : step === "results" ? "done" : ""} />
            <span className={step === "results" ? "active" : ""} />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="ccp-modal-body">
        {error && (
          <Alert variant="danger" className="ccp-error-alert" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === "select" && (
          <>
            <div className="ccp-mode-toggle" role="tablist">
              <button
                type="button"
                className={`ccp-mode-btn ${mode === "single" ? "active" : ""}`}
                onClick={() => { setMode("single"); setSelectedIds([]); setError(null); }}
              >
                <FaUser /> Un employé
              </button>
              <button
                type="button"
                className={`ccp-mode-btn ${mode === "multiple" ? "active" : ""}`}
                onClick={() => { setMode("multiple"); setSelectedOption(null); setError(null); }}
              >
                <FaUsers /> Plusieurs employés
              </button>
            </div>

            <div className="ccp-stats-row">
              <div className="ccp-stat">
                <span className="ccp-stat-val">{stats.total}</span>
                <span className="ccp-stat-lbl">Employés actifs</span>
              </div>
              <div className="ccp-stat ccp-stat--ok">
                <span className="ccp-stat-val">{stats.dispo}</span>
                <span className="ccp-stat-lbl">Sans compte</span>
              </div>
              <div className="ccp-stat ccp-stat--muted">
                <span className="ccp-stat-val">{stats.compte}</span>
                <span className="ccp-stat-lbl">Déjà un compte</span>
              </div>
            </div>

            {loadingData ? (
              <div className="ccp-loading">
                <Spinner animation="border" size="sm" />
                <span>Chargement des employés…</span>
              </div>
            ) : mode === "single" ? (
              <Form onSubmit={(e) => { e.preventDefault(); goConfirm(); }}>
                <Form.Group>
                  <Form.Label className="ccp-field-label">
                    <FaSearch size={12} />
                    Employé *
                  </Form.Label>
                  <div className="ccp-select-wrap">
                    <Select
                      options={employeOptions}
                      value={selectedOption}
                      onChange={(opt) => {
                        if (opt?.hasCompte) {
                          toast.warning("Cet employé possède déjà un compte personnel.");
                          return;
                        }
                        setSelectedOption(opt);
                      }}
                      placeholder="Rechercher par nom, matricule, département…"
                      isSearchable
                      isClearable
                      filterOption={filterEmployeOption}
                      formatOptionLabel={formatOptionLabel}
                      isOptionDisabled={(opt) => opt.hasCompte}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue ? `Aucun employé pour « ${inputValue} »` : "Aucun employé"
                      }
                      classNamePrefix="ccp-select"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </Form.Group>

                {selectedEmploye && !hasCompte(selectedEmploye.id_employe) && (
                  <div className="ccp-preview-card">
                    <div className="ccp-preview-title">Aperçu — compte à créer</div>
                    <div className="ccp-preview-grid">
                      <div className="ccp-preview-item">
                        <span className="ccp-preview-label">Nom complet</span>
                        <span className="ccp-preview-value">
                          {selectedEmploye.nom} {selectedEmploye.prenom}
                        </span>
                      </div>
                      <div className="ccp-preview-item">
                        <span className="ccp-preview-label">Matricule</span>
                        <span className="ccp-preview-value">{selectedEmploye.matricule_employe}</span>
                      </div>
                      <div className="ccp-preview-item full">
                        <span className="ccp-preview-label">Login généré</span>
                        <span className="ccp-preview-value ccp-login-preview">
                          <code>{previewLogin(selectedEmploye)}</code>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Form>
            ) : (
              <div className="ccp-multi-panel">
                <div className="ccp-multi-toolbar">
                  <div className="ccp-search-wrap">
                    <FaSearch className="ccp-search-icon" />
                    <Form.Control
                      type="search"
                      placeholder="Rechercher un employé…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="ccp-search-input"
                    />
                  </div>
                  <div className="ccp-filter-chips">
                    {[
                      { id: "all", label: "Tous" },
                      { id: "dispo", label: "Sans compte" },
                      { id: "compte", label: "Avec compte" },
                    ].map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className={`ccp-filter-chip ${filterDispo === f.id ? "active" : ""}`}
                        onClick={() => setFilterDispo(f.id)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ccp-multi-actions">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    className="ccp-btn-chip"
                    onClick={selectAllDisponibles}
                    disabled={employesDisponiblesFiltres.length === 0}
                  >
                    Tout sélectionner (éligibles)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="ccp-btn-chip"
                    onClick={clearSelection}
                    disabled={selectedIds.length === 0}
                  >
                    Tout désélectionner
                  </Button>
                  <Badge bg="primary" className="ccp-selection-badge">
                    {selectedIds.length} sélectionné(s)
                  </Badge>
                </div>

                <div className="ccp-emp-list" role="list">
                  {employesFiltres.length === 0 ? (
                    <div className="ccp-emp-empty">Aucun employé ne correspond à votre recherche.</div>
                  ) : (
                    employesFiltres.map((emp) => {
                      const existe = hasCompte(emp.id_employe);
                      const checked = selectedIds.includes(emp.id_employe);
                      return (
                        <div
                          key={emp.id_employe}
                          role="listitem"
                          className={`ccp-emp-row ${existe ? "ccp-emp-row--disabled" : ""} ${checked ? "ccp-emp-row--selected" : ""}`}
                          onClick={() => !existe && toggleEmploye(emp)}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={checked}
                            disabled={existe}
                            onChange={() => toggleEmploye(emp)}
                            onClick={(e) => e.stopPropagation()}
                            className="ccp-emp-check"
                            aria-label={`Sélectionner ${emp.nom} ${emp.prenom}`}
                          />
                          <div className="ccp-emp-info">
                            <span className="ccp-emp-name">
                              {emp.nom} {emp.prenom}
                            </span>
                            <span className="ccp-emp-meta">
                              {emp.matricule_employe}
                              {emp.departement ? ` · ${emp.departement}` : ""}
                            </span>
                          </div>
                          <div className="ccp-emp-badges">
                            {existe ? (
                              <span className="ccp-badge ccp-badge--exists">
                                <FaTimesCircle /> Compte existant
                              </span>
                            ) : (
                              <>
                                <span className="ccp-badge ccp-badge--ok">
                                  <FaCheckCircle /> Éligible
                                </span>
                                <span className="ccp-badge ccp-badge--login">
                                  {previewLogin(emp)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            <div className="ccp-info-box">
              <div className="ccp-info-box-title">
                <FaExclamationTriangle size={14} />
                Important avant de continuer
              </div>
              <ul className="ccp-info-list">
                <li>Les employés avec un compte existant ne peuvent pas être sélectionnés.</li>
                <li>Login, mot de passe temporaire et code bulletin sont générés automatiquement.</li>
                <li>Vous devrez copier manuellement les identifiants (aucun envoi automatique).</li>
              </ul>
            </div>
          </>
        )}

        {step === "confirm" && (
          <div className="ccp-confirm-panel">
            <Alert variant="warning" className="ccp-confirm-alert">
              <strong>Vérifiez attentivement</strong> — vous allez créer{" "}
              <strong>{mode === "single" ? 1 : selectedIds.length}</strong> compte(s) personnel.
              Cette action est immédiate.
            </Alert>

            <div className="ccp-confirm-list">
              {(mode === "single" && selectedEmploye
                ? [selectedEmploye]
                : selectedEmployes
              ).map((emp) => (
                <div key={emp.id_employe} className="ccp-confirm-item">
                  <div className="ccp-confirm-avatar">
                    {(emp.prenom?.[0] || "") + (emp.nom?.[0] || "")}
                  </div>
                  <div className="ccp-confirm-details">
                    <strong>
                      {emp.nom} {emp.prenom}
                    </strong>
                    <span>{emp.matricule_employe}</span>
                    <code className="ccp-confirm-login">{previewLogin(emp)}</code>
                  </div>
                  <FaCheck className="ccp-confirm-check" />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === "results" && createdSingle && (
          <div className="ccp-success-box">
            <div className="ccp-success-title">
              <FaCheckCircle /> Compte créé — copiez ces informations :
            </div>
            {[
              ["Login", createdSingle.login],
              ["Mot de passe temporaire", createdSingle.mot_de_passe_temp],
              ["Code de contrôle bulletin", createdSingle.code_controle],
              ["Lien de connexion", LOGIN_URL],
            ].map(([label, val]) => (
              <div key={label} className="ccp-credential-row">
                <strong>{label}</strong>
                <code>{val}</code>
                <Button
                  size="sm"
                  variant="outline-success"
                  className="ccp-copy-btn"
                  onClick={() => copyText(String(val), label)}
                >
                  <FaCopy />
                </Button>
              </div>
            ))}
          </div>
        )}

        {step === "results" && bulkResults && (
          <div className="ccp-bulk-results">
            <div className="ccp-bulk-summary">
              <span className="ccp-bulk-ok">{bulkResults.created} créé(s)</span>
              {bulkResults.failed > 0 && (
                <span className="ccp-bulk-fail">{bulkResults.failed} échec(s)</span>
              )}
            </div>
            {bulkResults.created > 0 && (
              <Button
                size="sm"
                variant="outline-primary"
                className="ccp-copy-all-btn mb-3"
                onClick={() => copyAllCredentials(bulkResults.results)}
              >
                <FaCopy className="me-1" /> Copier tous les identifiants
              </Button>
            )}
            <div className="ccp-bulk-list">
              {(bulkResults.results || []).map((r) => (
                <div
                  key={r.id_employe}
                  className={`ccp-bulk-card ${r.success ? "success" : "error"}`}
                >
                  <div className="ccp-bulk-card-head">
                    {r.success ? (
                      <FaCheckCircle className="text-success" />
                    ) : (
                      <FaTimesCircle className="text-danger" />
                    )}
                    <strong>
                      {r.data
                        ? `${r.data.nom} ${r.data.prenom}`
                        : `Employé #${r.id_employe}`}
                    </strong>
                  </div>
                  {r.success ? (
                    <div className="ccp-bulk-creds">
                      <div><span>Login</span><code>{r.data.login}</code></div>
                      <div><span>Mot de passe</span><code>{r.data.mot_de_passe_temp}</code></div>
                      <div><span>Code bulletin</span><code>{r.data.code_controle}</code></div>
                    </div>
                  ) : (
                    <p className="ccp-bulk-err mb-0">{r.error}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="ccp-modal-footer">
        {step === "select" && (
          <>
            <Button variant="light" onClick={handleClose} className="ccp-btn-cancel">
              Annuler
            </Button>
            <Button
              onClick={goConfirm}
              disabled={loadingData || selectionCount === 0}
              className="ccp-btn-submit text-white"
            >
              Continuer ({selectionCount})
            </Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button
              variant="light"
              className="ccp-btn-cancel"
              onClick={() => setStep("select")}
              disabled={loading}
            >
              Retour
            </Button>
            <Button
              onClick={runCreate}
              disabled={loading}
              className="ccp-btn-submit text-white"
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Création…
                </>
              ) : (
                <>Confirmer la création ({mode === "single" ? 1 : selectedIds.length})</>
              )}
            </Button>
          </>
        )}
        {step === "results" && (
          <Button className="ccp-btn-done text-white" onClick={handleDone}>
            J&apos;ai copié les informations
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreerComptePersonnelModal;
